import { useStyletron } from '@thanx/uikit/theme'
import React from 'react'

// ═══════════════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════════════

export interface DeleteButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** Accessible label for the button */
  'aria-label'?: string
}

// ═══════════════════════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Reusable delete/remove button for card headers.
 *
 * @example
 * <CardHeader>
 *   <CardHeaderTitle>ITEM 1</CardHeaderTitle>
 *   <DeleteButton onClick={handleDelete} aria-label="Delete Item 1" />
 * </CardHeader>
 */
const DeleteButton = React.memo(
  ({
    onClick,
    style,
    className,
    'aria-label': ariaLabel = 'Delete',
    disabled,
    ...props
  }: DeleteButtonProps) => {
    const [css, theme] = useStyletron()

    const buttonClassName = css({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '4px 8px',
      border: 'none',
      background: 'none',
      cursor: disabled ? 'not-allowed' : 'pointer',
      color: disabled ? theme.colors.grey30 : theme.colors.grey50,
      borderRadius: '4px',
      opacity: disabled ? 0.5 : 1,
      ':hover': disabled
        ? {}
        : {
            color: theme.colors.grey70,
            backgroundColor: theme.colors.grey10,
          },
    })

    return (
      <button
        type="button"
        onClick={onClick}
        className={`${buttonClassName} ${className || ''}`}
        style={style}
        aria-label={ariaLabel}
        disabled={disabled}
        {...props}
      >
        <i className="fa fa-trash" />
      </button>
    )
  }
)

DeleteButton.displayName = 'DeleteButton'

export default DeleteButton
